import React from 'react';
import styled from 'styled-components';

import { DataElementContext } from '../../../page-components/common/DataElementContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store';
import { closeIGRPopup, IdleGameRecommendationReducer, openIGRPopupAsync } from '@/store/slices/idleGameRecommendation';
//import './index.scss';

type IdleGameRecommendationProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: {
    dsType: string;
  };
};

const defaultProps = {
  className: '',
  styleText: '',
  properties: {
    dsType: '',
  },
};

const ALLOWED_PAGES = [
  '/slots',
  '/casino',
  '/live-casino',
  '/new-games',
  '/casino-games',
  '/jackpots',
  '/promotions',
  '/eagle-exclusive-games',
  'slot-game',
];

const IGR_TIMER = parseInt(window?.config?.idleGameRecomendationSeconds) * 1000;
const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const useIdle = (timeout: number = IGR_TIMER) => {
  const [isIdle, setIsIdle] = React.useState(false);
  const timeoutId = React.useRef<NodeJS.Timeout | null>(null);

  const debounce = (func: () => void, delay: number) => {
    return () => {
      if (timeoutId.current) clearTimeout(timeoutId.current);
      timeoutId.current = setTimeout(func, delay);
    };
  };

  React.useEffect(() => {
    const events = ['mousemove', 'keydown', 'scroll', 'touchmove'];
    const handleEvent = debounce(() => {
      setIsIdle(false);
      if (timeoutId.current) clearTimeout(timeoutId.current);
      timeoutId.current = setTimeout(() => {
        setIsIdle(true);
      }, timeout);
    }, 1000);

    events.forEach((event) => {
      document.addEventListener(event, handleEvent);
    });

    return () => {
      if (timeoutId.current) clearTimeout(timeoutId.current);
      events.forEach((event) => {
        document.removeEventListener(event, handleEvent);
      });
    };
  }, [timeout]);

  return isIdle;
};

const IdleGameRecommendation = (componentProps: IdleGameRecommendationProps) => {
  const tmpProps = { ...defaultProps, ...componentProps };
  delete tmpProps.children;
  const props = JSON.parse(JSON.stringify(tmpProps));
  const { children } = componentProps;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const idleGameRecommendation = useAppSelector(
    (state) => state.idleGameRecommendation,
  ) as IdleGameRecommendationReducer;

  const [isDisabled, setIsDisabled] = React.useState(false);
  const [prefetched, setPrefetched] = React.useState(false);

  const isIdle = useIdle(IGR_TIMER);
  const prefetchTimeout = Math.floor(IGR_TIMER / 2);

  React.useEffect(() => {
    if (
      !isDisabled &&
      isIdle &&
      !idleGameRecommendation.isOpen &&
      prefetched &&
      !location?.pathname?.includes('slot-game')
    ) {
      dispatch(openIGRPopupAsync());
    }
  }, [isIdle, isDisabled, idleGameRecommendation.isOpen, prefetched, location?.pathname, dispatch]);

  React.useEffect(() => {
    dispatch(closeIGRPopup());
    const isDisabledConfig = window?.config?.disableIdleGameRecommendation === '0';
    const isIdleOnlyDisabled = window?.config?.disableIGRIdleOnly === '0';
    const isInGameOnlyDisabled = window?.config?.disableIGRInGameOnly === '0';
    const isAllowedPage = ALLOWED_PAGES.some((value) => location?.pathname.includes(value)) || location?.pathname === '/';
    const isInGamePath = location?.pathname?.includes('slot-game');

    if (
      !(
        isDisabledConfig &&
        isAllowedPage &&
        ((isIdleOnlyDisabled) ||
        (isInGameOnlyDisabled && isInGamePath))
      )
    ) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  }, [location?.pathname, dispatch]);

  React.useEffect(() => {
    if (!isDisabled && !idleGameRecommendation.isOpen && !prefetched && !location?.pathname?.includes('slot-game')) {
      const timer = setTimeout(() => {
        dispatch(openIGRPopupAsync({ prefetch: true }));
        setPrefetched(true);
      }, prefetchTimeout);

      return () => clearTimeout(timer);
    }
  }, [isDisabled, idleGameRecommendation.isOpen, prefetched, location?.pathname, dispatch, prefetchTimeout]);

  React.useEffect(() => {
    if (!isDisabled && location?.pathname?.includes('slot-game') && !prefetched) {
      dispatch(openIGRPopupAsync({ prefetch: true }));
      setPrefetched(true);
    }
  }, [isDisabled, location?.pathname, dispatch, prefetched]);

  const closePopupHandler = React.useCallback(() => {
    dispatch(closeIGRPopup());
    setPrefetched(false);
    if (location?.pathname?.includes('slot-game')) {
      navigate('/');
    }
  }, [dispatch, location?.pathname, navigate]);

  const contextValue = React.useMemo(() => {
    return {
      IGRPopupActive: idleGameRecommendation.isOpen && !isDisabled,
      recommendedGame: idleGameRecommendation?.recommendedGame,
      isLoading: idleGameRecommendation?.isLoading,
      prefetched: prefetched,
      closePopupHandler: closePopupHandler,
    };
  }, [
    idleGameRecommendation.isOpen,
    idleGameRecommendation?.recommendedGame,
    idleGameRecommendation?.isLoading,
    prefetched,
    isDisabled,
    closePopupHandler,
  ]);

  if (isDisabled) return null;
  return (
    <ModuleElementDiv className={props.className ?? ''} $styleText={props.styleText}>
      <DataElementContext.Provider value={contextValue}>{children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default IdleGameRecommendation;
